import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import Thumbnail from '@configuratorware/configurator-frontendgui/App/Screens/Configurator/Containers/Thumbnail';
import Steps from '../Components/Steps/Steps';
import ProductPartsData from '../Components/ProductPartsData/ProductPartsData';
import StepsNavigator from '../Components/StepsNavigator/StepsNavigator';
import { offwhite, white } from '../Constants/Colors';
import OverviewSidebar from '../Components/OverviewSidebar/OverviewSidebar';
import Header from '../Components/Header/Header';
import ConfigurationOverview from '../Components/ConfigurationOverview/ConfigurationOverview';
import { AlignTargetContainer } from '@configuratorware/configurator-frontendgui/App/Modules/Visualization/AlignTargetContainer';
import { useSelector } from 'react-redux';
import { useMediaQuery, useTheme } from '@material-ui/core';
import {
  getShowProductParts,
  getShowStepsNavigator,
  getShowConfigurationOverview,
} from '../Reducers/UI/Selectors';
import clsx from 'clsx';
import CameraViewToolbar from '../Components/CameraViewToolbar/CameraViewToolbar';
import CustomSaveConfigurationContainer from '../Components/CustomSaveConfiguration/CustomSaveConfigurationContainer';
import LoadConfigurationContainer from '@configuratorware/configurator-frontendgui/App/Shared/Containers/LoadConfiguration';
import CustomLoadConfiguration from '../Components/CustomLoadConfiguration/CustomLoadConfiguration';
import CustomProgress from '../Components/CustomProgress/CustomProgress';
import PdfDownload from '@configuratorware/configurator-frontendgui/App/Shared/Containers/PdfDownload/';
import CustomPdfDownload from '../Components/PdfDownload';

export const DETAILS_ID = 'frame';

const styles = (theme) => ({
  root: {
    padding: '20px 150px',
    backgroundColor: offwhite,
    [theme.breakpoints.down('sm')]: {
      padding: 'unset',
    },
  },
  visualizationContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    minHeight: 300,
    [theme.breakpoints.down('sm')]: {
      marginLeft: -20,
      marginRight: -20,
      position: 'relative',
    },
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    // the pointer events should be passed through to the visualization component
    pointerEvents: 'none', // important: don't remove this
  },
  steps: {
    display: 'flex',
    paddingTop: 16,
    minWidth: 215,
    zIndex: 1,
  },
  overview: {
    backgroundColor: white,
  },
  outerContainer: {
    display: 'flex',
    minHeight: 800,
    [theme.breakpoints.down('sm')]: {
      padding: '20px 50px',
      flexDirection: 'column',
      width: '100%',
      margin: 0,
      minHeight: 450,
    },
    [theme.breakpoints.down('xs')]: {
      padding: 20,
    },
    position: 'relative',
  },
  header: {
    marginLeft: 'auto',
    justifyContent: 'flex-end',
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  visualization: {
    flex: 1,
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: 0,
  },
  stickyTop: {
    position: 'sticky',
    top: 0,
    zIndex: 1100,
    backgroundColor: offwhite,
    padding: '0px 20px',
  },
  footer: {
    position: 'sticky',
    bottom: 0,
    width: '100%',
    background: offwhite,
    zIndex: 1200,
    boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
  },
});

const Layout = ({
  classes,
  renderSendEmail,
  renderError,
  renderAlertMessages,
  renderConfirm,
  fallbackMode,
  renderVisualization,
}) => {
  const showProductParts = useSelector(getShowProductParts);
  const showStepsNavigator = useSelector(getShowStepsNavigator);
  const showConfigurationOverview = useSelector(getShowConfigurationOverview);
  const { isFetching } = useSelector((state) => state.api);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const renderCustomSaveConfiguration = () => <CustomSaveConfigurationContainer />;
  const renderCustomLoadConfiguration = () => (
    <LoadConfigurationContainer LoadConfigurationComponent={CustomLoadConfiguration} />
  );

  const renderCustomPdfDownload = () => <PdfDownload Component={CustomPdfDownload} />;

  const renderLoader = () => <CustomProgress {...classes} loading={isFetching} />;
  return (
    <div>
      {!isMobile && (
        <div className={clsx(classes.root, classes.header)}>
          <Header />
        </div>
      )}
      {isMobile && (
        <div className={clsx(classes.root, classes.header, classes.stickyTop)}>
          <Header />
          <Steps />
        </div>
      )}

      <div className={clsx(classes.root, classes.outerContainer)}>
        {!isMobile && (
          <div className={classes.steps}>
            <Steps />
          </div>
        )}
        <div className={classes.visualizationContainer}>
          <AlignTargetContainer>
            {!fallbackMode && <CameraViewToolbar />}
            {!fallbackMode && (
              <div className={classes.visualization}>
                {renderVisualization({ fullScreenViewProps: { showPreviewButton: false } })}
              </div>
            )}
          </AlignTargetContainer>
          {renderLoader()}
        </div>
      </div>
      <Thumbnail />
      <React.Fragment>
        <div className={classes.root} id={DETAILS_ID}>
          {showProductParts && <ProductPartsData />}
          {isMobile && showStepsNavigator && (
            <div className={classes.footer}>
              <StepsNavigator />
            </div>
          )}
        </div>
        {showConfigurationOverview && (
          <div className={clsx(classes.root, classes.overview)}>
            <ConfigurationOverview />
          </div>
        )}
      </React.Fragment>
      {!isMobile && showStepsNavigator && (
        <div className={classes.root}>
          <StepsNavigator />
        </div>
      )}

      <div className={clsx(classes.root, classes.main)}>
        {renderError()}
        {renderAlertMessages()}
        {renderConfirm()}
        {renderCustomSaveConfiguration()}
        {renderSendEmail()}
        {renderCustomPdfDownload()}
        {renderCustomLoadConfiguration()}
      </div>
      <OverviewSidebar />
    </div>
  );
};

Layout.propTypes = {
  classes: PropTypes.object,
  width: PropTypes.string,
  renderHeader: PropTypes.func,
  renderMain: PropTypes.func,
  renderSidebar: PropTypes.func,
  renderVisualization: PropTypes.func,
  renderLoader: PropTypes.func,
  renderLicenseNotice: PropTypes.func,
  fallbackMode: PropTypes.bool,
  isPortraitMode: PropTypes.bool,
  imageOnly: PropTypes.bool,
  renderSaveConfiguration: PropTypes.func,
  renderSendEmail: PropTypes.func,
  renderPdfDownload: PropTypes.func,
  renderLoadConfiguration: PropTypes.func,
  renderError: PropTypes.func,
  renderAlertMessages: PropTypes.func,
  renderConfirm: PropTypes.func,
};

export default withStyles(styles, { name: 'Layout' })(withWidth({ initialWidth: 'xl' })(Layout));
